import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearch } from "@tanstack/react-router";
import { addDays } from "@trameria/core/cases-shared";
import type { TimeBlock } from "@trameria/core/timeblocker-shared";
import { getLocale, m } from "@trameria/i18n";
import { useEffect, useMemo, useRef, useState } from "react";
import { useApi } from "../app/context";
import { DayPopover } from "../cases/bits";
import { HEADER_PILL } from "../cases/detail";
import { apiErrorMessage } from "../data/errors";
import { useProfile } from "../me/profile";
import { useShellFrame } from "../shell/frame";
import { useShellEmbedded } from "../shell/outside/outside";
import { PanelHeader } from "../shell/panel";
import { useShortcuts } from "../shell/shortcuts";
import { ICON_BUTTON } from "../ui/button";
import { Icon } from "../ui/icon";
import { CarriedCard, TimeAxis } from "./axis";
import { DROP_MINUTES, daySections, dropStart, isoAt } from "./day";
import { useBlockHistory, useHistoryState } from "./history";
import { DayLists } from "./list";
import {
  dayQuery,
  timeBlockerChanged,
  useAddBlock,
  useStartEndBlock,
} from "./queries";
import { TIMEBLOCKER_REVIEW_PATH } from "./review";
import { RunningBlockLine } from "./running";
import {
  TimeBlockerScreenProvider,
  useTimeBlockerScreen,
} from "./screen-state";
import { BlockDetail } from "./time-block";
/** The day as the header says it (month, day and weekday in the person's language: "Mon, Oct 12"). */
export function dayLabel(day: string): string {
  const [y, mo, d] = day.split("-").map(Number);
  const date = new Date(Date.UTC(y, mo - 1, d));
  if (getLocale() !== "ja")
    return new Intl.DateTimeFormat(getLocale(), {
      weekday: "short",
      month: "short",
      day: "numeric",
      timeZone: "UTC",
    }).format(date);
  const dow = new Intl.DateTimeFormat("ja", {
    weekday: "short",
    timeZone: "UTC",
  }).format(date);
  // i18n-ignore: the Japanese day is put together by hand (the other languages use Intl)
  return `${mo}月${d}日(${dow})`;
}

/** What a notice opens: its block, and with `start` its "Start" button was pressed. */
export interface BlockLinkSearch {
  block?: string;
  action?: "start";
}

/**
 * A notice's block opens in its detail; its "Start" button starts it once the
 * day is read (a block not on the day shown is started all the same). The
 * address then loses both, so a reload starts nothing again.
 */
function useBlockFromNotice(blocks: TimeBlock[] | undefined) {
  const search = useSearch({ strict: false }) as BlockLinkSearch;
  const navigate = useNavigate();
  const screen = useTimeBlockerScreen();
  const start = useStartEndBlock();
  const api = useApi();
  const qc = useQueryClient();
  const handled = useRef<string | null>(null);
  const key = search.block ? `${search.block}:${search.action ?? ""}` : null;
  // frontend-development: not a fetch; a notice's button does its write once, then the address is rewritten.
  useEffect(() => {
    if (!key || !search.block || !blocks || handled.current === key) return;
    handled.current = key;
    const id = search.block;
    const block = blocks.find((b) => b.id === id);
    if (block) screen.setSelected(id);
    if (search.action === "start") {
      const failed = (e: Error) =>
        screen.setError(apiErrorMessage(e, m.timeblocker_save_failed()));
      if (block)
        start.mutate(
          { ...screen.place, block, end: false },
          { onError: failed },
        );
      else
        api(`/api/me/timeblocker/blocks/${encodeURIComponent(id)}/start`, {
          method: "POST",
        }).then(() => timeBlockerChanged(qc), failed);
    }
    void navigate({
      to: ".",
      search: (prev: Record<string, unknown>) => {
        const { block: _b, action: _a, ...rest } = prev;
        return rest;
      },
      replace: true,
    });
  }, [key, blocks, search, screen, start, api, qc, navigate]);
}

export function TimeBlockerScreen() {
  const profile = useProfile();
  if (!profile.data) return null;
  return (
    <TimeBlockerScreenProvider>
      <Screen />
    </TimeBlockerScreenProvider>
  );
}
function Screen() {
  const navigate = useNavigate();
  const embedded = useShellEmbedded();
  const frame = useShellFrame();
  const mobile = frame.phone || frame.panelWidth < 768;
  const api = useApi(),
    screen = useTimeBlockerScreen();
  const { day, timeZone } = screen;
  const { data, error } = useQuery(dayQuery(api, day, timeZone, !mobile));
  const sections = useMemo(() => daySections(data, day), [data, day]);
  useBlockFromNotice(data?.blocks);
  const selected = data?.blocks.find((b) => b.id === screen.selected);
  const add = useAddBlock();
  const history = useBlockHistory();
  const { canUndo, canRedo } = useHistoryState();
  const undo = () =>
    void history
      .undo()
      .catch((e) =>
        screen.setError(apiErrorMessage(e, m.timeblocker_undo_failed())),
      );
  const redo = () =>
    void history
      .redo()
      .catch((e) =>
        screen.setError(apiErrorMessage(e, m.timeblocker_redo_failed())),
      );
  useShortcuts([
    {
      id: "timeblocker.undo",
      key: "mod+z",
      group: "general",
      label: () => m.timeblocker_undo(),
      notTyping: true,
      when: () => canUndo,
      run: undo,
    },
    {
      id: "timeblocker.redo",
      key: "mod+shift+z",
      group: "general",
      label: () => m.timeblocker_redo(),
      notTyping: true,
      when: () => canRedo,
      run: redo,
    },
  ]);
  const dateAnchor = useRef<HTMLButtonElement>(null);
  const [dateOpen, setDateOpen] = useState(false);
  const newStart =
    day === screen.today
      ? dropStart((screen.now.getTime() - screen.dayStart) / 60_000)
      : 9 * 60;
  const addIt = () =>
    add.mutate(
      {
        ...screen.place,
        startAt: isoAt(screen.dayStart, newStart),
        endAt: isoAt(screen.dayStart, newStart + DROP_MINUTES),
      },
      {
        onSuccess: (block) => {
          if (block) screen.setSelected(block.id);
        },
        onError: (e) =>
          screen.setError(apiErrorMessage(e, m.timeblocker_save_failed())),
      },
    );
  return (
    <>
      <PanelHeader
        header={{
          title: m.timeblocker_title(),
          note: dayLabel(day),
          actions: [
            {
              id: "timeblocker.prev",
              label: m.timeblocker_prev_day(),
              icon: "back",
              onPress: () => screen.setDay(addDays(day, -1)),
            },
            {
              id: "timeblocker.date",
              label: dayLabel(day),
              icon: "timeblocker",
              onPress: () => setDateOpen(true),
            },
            {
              id: "timeblocker.next",
              label: m.timeblocker_next_day(),
              icon: "forward",
              onPress: () => screen.setDay(addDays(day, 1)),
            },
            {
              id: "timeblocker.today",
              label: m.timeblocker_go_today(),
              style: "pill",
              disabled: day === screen.today,
              onPress: () => screen.setDay(screen.today),
            },
            {
              id: "timeblocker.undo",
              label: m.timeblocker_undo(),
              icon: "rewind",
              disabled: !canUndo,
              onPress: undo,
            },
            {
              id: "timeblocker.redo",
              label: m.timeblocker_redo(),
              icon: "redo",
              disabled: !canRedo,
              onPress: redo,
            },
            {
              id: "timeblocker.add",
              label: m.timeblocker_add_block(),
              icon: "plus",
              onPress: addIt,
            },
            {
              id: "timeblocker.review",
              label: m.timeblocker_review(),
              icon: "review",
              onPress: () => void navigate({ to: TIMEBLOCKER_REVIEW_PATH }),
            },
          ],
        }}
        title={
          embedded ? (
            dayLabel(day)
          ) : (
            <span className="max-sm:hidden @max-[480px]:hidden">
              {m.timeblocker_title()}
            </span>
          )
        }
        actions={
          <div className="flex items-center gap-2">
            <span className="max-sm:hidden @max-[480px]:hidden">
              <RunningBlockLine />
            </span>
            <button
              type="button"
              aria-label={m.timeblocker_undo()}
              title={m.timeblocker_undo()}
              disabled={!canUndo}
              className={`${ICON_BUTTON} disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent`}
              onClick={undo}
            >
              <Icon kind="rewind" />
            </button>
            <button
              type="button"
              aria-label={m.timeblocker_redo()}
              title={m.timeblocker_redo()}
              disabled={!canRedo}
              className={`${ICON_BUTTON} disabled:cursor-default disabled:opacity-40 disabled:hover:bg-transparent`}
              onClick={redo}
            >
              <Icon kind="redo" />
            </button>
            <button
              type="button"
              aria-label={m.timeblocker_add_block()}
              className={ICON_BUTTON}
              onClick={addIt}
            >
              <Icon kind="plus" />
            </button>
            <DayNav anchor={dateAnchor} open={dateOpen} setOpen={setDateOpen} />
            <Link
              to={TIMEBLOCKER_REVIEW_PATH}
              aria-label={m.timeblocker_review()}
              title={m.timeblocker_review()}
              className={ICON_BUTTON}
            >
              <Icon kind="review" />
            </Link>
          </div>
        }
      />
      {(screen.error || error) && (
        <div
          role="alert"
          className="absolute right-4 top-12 z-30 rounded bg-surface px-3 py-2 text-[12px] text-danger"
        >
          {screen.error ?? apiErrorMessage(error, m.timeblocker_save_failed())}
        </div>
      )}
      <div className="flex min-h-0 flex-1 flex-col md:flex-row">
        {!mobile && (
          <DayLists
            sections={sections}
            selectedStepId={
              selected?.links.find((l) => l.stepId)?.stepId ?? null
            }
          />
        )}
        {data && (
          <TimeAxis
            label={m.timeblocker_timeline({ date: dayLabel(day) })}
            placed={sections.placed}
          />
        )}
      </div>
      <CarriedCard />
      {selected && (
        <BlockDetail
          key={selected.id}
          block={selected}
          onClose={() => screen.setSelected(null)}
        />
      )}
    </>
  );
}
/** ‹ date › Today. Pressing the date picks one from the calendar. */
function DayNav({
  anchor,
  open,
  setOpen,
}: {
  anchor: React.RefObject<HTMLButtonElement | null>;
  open: boolean;
  setOpen(open: boolean): void;
}) {
  const screen = useTimeBlockerScreen();
  const { day, today } = screen;
  return (
    <span className="flex flex-none items-center gap-[2px]">
      <button
        type="button"
        aria-label={m.timeblocker_prev_day()}
        onClick={() => screen.setDay(addDays(day, -1))}
        className={ICON_BUTTON}
      >
        <Icon kind="back" size={14} stroke={1.5} />
      </button>
      <button
        ref={anchor}
        type="button"
        aria-label={`${m.timeblocker_pick_date()}: ${dayLabel(day)}`}
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
        className="h-[26px] min-w-[7.5em] flex-none whitespace-nowrap cursor-pointer rounded-[13px] px-2 text-[13px] tabular-nums text-fg hover:bg-hover"
      >
        {dayLabel(day)}
      </button>
      <button
        type="button"
        aria-label={m.timeblocker_next_day()}
        onClick={() => screen.setDay(addDays(day, 1))}
        className={ICON_BUTTON}
      >
        <Icon kind="forward" size={14} stroke={1.5} />
      </button>
      {/* Kept in place on today too (only not pressable), so nothing moves. */}
      <button
        type="button"
        disabled={day === today}
        onClick={() => screen.setDay(today)}
        className={`${HEADER_PILL} ml-1 disabled:cursor-default disabled:opacity-40 disabled:hover:border-pill-border disabled:hover:text-body-soft`}
      >
        {m.timeblocker_go_today()}
      </button>
      <DayPopover
        anchor={anchor}
        open={open}
        onClose={() => setOpen(false)}
        label={m.timeblocker_pick_date()}
        value={day}
        clearLabel={m.timeblocker_go_today()}
        onChange={(v) => {
          screen.setDay(v ?? today);
          setOpen(false);
        }}
      />
    </span>
  );
}
